import { datadogLogs, LogsEvent } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { AxiosError } from 'axios';
import { pick } from 'lodash-es';
import { getAppBaseUrl, getFeedbackApiDomain, getSearchApiDomain, getSvDomain } from './url';

const loggerForType =
  (type: 'info' | 'error' | 'warn' | 'debug') =>
  (message: string, options?: any): void => {
    if (import.meta.env.PROD) {
      datadogLogs.logger[type](message, options);

      if (type === 'error') {
        datadogRum.addError(options);
      } else {
        datadogRum.addAction(message, options);
      }
    } else {
      console[type](message, options);
    }
  };

export function initialise(): void {
  const commonOptions = {
    site: window.CONFIG.dataDogDomain,
    service: 'get-active-kids-manage-provider',
    sessionSampleRate: 100,
    env: window.CONFIG.appEnv?.trim(),
    version: window.CONFIG.versionNumber?.trim(),
    trackSessionAcrossSubdomains: true
  };

  datadogLogs.init({
    ...commonOptions,
    clientToken: window.CONFIG.dataDogToken || '',
    forwardErrorsToLogs: true,
    beforeSend: log => {
      // Workaround since isAxiosError won't be available from the error object after upgrading to axios 0.27.2
      if (log.name === 'AxiosError' && log.config) {
        // suppress user data from the axios config object. Else the JWT & PII is logged
        (log as AxiosError & LogsEvent).config = pick(
          (log as AxiosError & LogsEvent).config,
          'url',
          'baseURL',
          'method'
        );
      }
    }
  });

  if (
    window.CONFIG.dataDogRealUserMonitoringAppId &&
    window.CONFIG.dataDogRealUserMonitoringToken
  ) {
    datadogRum.init({
      ...commonOptions,
      applicationId: window.CONFIG.dataDogRealUserMonitoringAppId.trim(),
      clientToken: window.CONFIG.dataDogRealUserMonitoringToken.trim(),
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      trackViewsManually: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [
        url => url.startsWith(getSearchApiDomain()),
        url => url.startsWith(getFeedbackApiDomain()),
        url => url.startsWith(getSvDomain()),
        url => url.startsWith(getAppBaseUrl())
      ]
    });
  }
}

export const logger = {
  info: loggerForType('info'),
  debug: loggerForType('debug'),
  error: loggerForType('error'),
  warn: loggerForType('warn'),
  setContext: (key: string, value: any) => {
    datadogLogs.addLoggerGlobalContext(key, value);
    datadogRum.addRumGlobalContext(key, value);
  }
};

export default logger;
